import { registerDestructor } from '@ember/destroyable';
import Component from '@glimmer/component';

import { autocompletion } from '@codemirror/autocomplete';
import { defaultKeymap, history, indentWithTab } from '@codemirror/commands';
import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import {
  StreamLanguage,
  defaultHighlightStyle,
  foldGutter,
  indentOnInput,
  syntaxHighlighting,
} from '@codemirror/language';
import { shell } from '@codemirror/legacy-modes/mode/shell';
import { lintGutter, linter } from '@codemirror/lint';
import {
  EditorView,
  crosshairCursor,
  drawSelection,
  dropCursor,
  highlightActiveLine,
  highlightActiveLineGutter,
  highlightSpecialChars,
  keymap,
  lineNumbers,
  rectangularSelection,
} from '@codemirror/view';
import Modifier from 'ember-modifier';

import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';

const bem = getBem(styles);

function cleanup(instance) {
  if (instance.editor) {
    instance.editor.destroy();
  }
}

export const codemirror = class CodemirrorModifier extends Modifier {
  // Defaults
  editor;

  constructor(owner, args) {
    super(owner, args);
    registerDestructor(this, cleanup);
  }

  // Hooks
  modify(element, _positionalArgs, namedArgs) {
    const options = namedArgs.options;
    if (!this.editor) {
      this._setup(element, options);
    }

    if (this.editor?.state.doc.toString() !== options.value) {
      this.editor.dispatch({
        changes: {
          from: 0,
          to: this.editor.state.doc.length,
          insert: options.value,
        },
      });
    }
  }

  // Function
  _onChange(editor, options) {
    const hasChanges = this.editor?.state.doc.toString() !== options.value;

    if (!options.readOnly && editor.docChanged && hasChanges) {
      options.onUpdate(editor.state.doc.toString());
    }
  }

  _setup(element, options) {
    if (!element) {
      throw new Error('CodeMirror modifier has no element');
    }

    if (!options.onUpdate && !options.readOnly) {
      throw new Error(
        'CodeMirror modifier needs an onUpdate function if not in readOnly mode',
      );
    }

    element.classList.add(bem());

    const modes = {
      html: html(),
      css: css(),
      javascript: javascript(),
      json: json(),
      shell: StreamLanguage.define(shell),
    };

    const linters = {
      json: jsonParseLinter(),
    };

    const editor = new EditorView({
      doc: options.value,
      parent: element,
      extensions: [
        modes[options.mode ?? 'html'],
        highlightActiveLineGutter(),
        highlightSpecialChars(),
        history(),
        foldGutter(),
        keymap.of([defaultKeymap, indentWithTab]),
        autocompletion(),
        drawSelection(),
        dropCursor(),
        indentOnInput(),
        lineNumbers(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        rectangularSelection(),
        crosshairCursor(),
        highlightActiveLine(),
        lintGutter(),
        linter(linters[options.mode]),
        EditorView.updateListener.of((editor) => {
          this._onChange(editor, options);
        }),
        EditorView.contentAttributes.of({
          contenteditable: !options.readOnly,
        }),
      ],
    });

    this.editor = editor;
  }
};

export class Codemirror extends Component {
  get options() {
    return this.args;
  }

  <template>
    <div {{codemirror options=this.options}} ...attributes></div>
  </template>
}
